import { CreateLibraryResponse } from '@service/laboratory/library/library-repository';
import {
  CreateWorkListsResponse,
  WorkList,
  WorkListSpecimen
} from '@model/laboratory/worklist.model';

/**
 * Backend DTO for WorkList Specimen
 */
export interface WorkListSpecimenBackendDTO {
  specimenId: string;
  lgCode: string;
  listPosition: number;
  assignmentDate: string;
}

/**
 * Backend DTO for WorkList
 */
export interface WorkListBackendDTO {
  workListId: string;
  listNumber: number;
  technology: string;
  listStatus: string;
  createdByUserId: string;
  specimenCount: number;
  specimens: WorkListSpecimenBackendDTO[];
  createdAt: string;
}

/**
 * Backend DTO for Create WorkLists Response
 */
export interface CreateWorkListsBackendResponse {
  workLists: WorkListBackendDTO[];
  totalSamplesAssigned: number;
  createdAt: string;
}

/**
 * Backend DTO for Create Library Response
 */
export interface CreateLibraryBackendResponse {
  libraryId: string;
  cellId: string;
  plateId: string;
  indexId: string;
  sampleCount: number;
  createdAt: string;
  createdByUserId: string;
}

/**
 * WorkList Mapper
 *
 * Transforms backend DTOs to domain models for WorkList entities
 */
export class WorkListMapper {
  /**
   * Transform WorkListSpecimen from backend DTO to domain model
   */
  static fromBackendSpecimenDto(
    dto: WorkListSpecimenBackendDTO
  ): WorkListSpecimen {
    return {
      specimenId: dto.specimenId,
      lgCode: dto.lgCode,
      listPosition: dto.listPosition,
      assignmentDate: new Date(dto.assignmentDate)
    };
  }

  /**
   * Transform WorkList from backend DTO to domain model
   */
  static fromBackendWorkListDto(dto: WorkListBackendDTO): WorkList {
    return {
      workListId: dto.workListId,
      listNumber: dto.listNumber,
      technology: dto.technology,
      listStatus: dto.listStatus,
      createdByUserId: dto.createdByUserId,
      specimenCount: dto.specimenCount,
      specimens: dto.specimens.map((s) =>
        WorkListMapper.fromBackendSpecimenDto(s)
      ),
      createdAt: new Date(dto.createdAt)
    };
  }

  /**
   * Transform CreateWorkListsResponse from backend DTO to domain model
   */
  static fromBackendResponse(
    response: CreateWorkListsBackendResponse
  ): CreateWorkListsResponse {
    return {
      workLists: response.workLists.map((wl) =>
        WorkListMapper.fromBackendWorkListDto(wl)
      ),
      totalSamplesAssigned: response.totalSamplesAssigned,
      createdAt: new Date(response.createdAt)
    };
  }

  /**
   * Transform CreateLibraryResponse from backend DTO to domain model
   */
  static fromBackendLibraryResponse(
    response: CreateLibraryBackendResponse
  ): CreateLibraryResponse {
    return {
      libraryId: response.libraryId,
      cellId: response.cellId,
      plateId: response.plateId,
      indexId: response.indexId,
      sampleCount: response.sampleCount,
      createdAt: new Date(response.createdAt),
      createdByUserId: response.createdByUserId
    };
  }
}
